import React, { useRef, useEffect } from "react";
import { Link } from "gatsby";
import itinerary from "~/images/itineraries.svg";
import guide from "~/images/guide.svg";
import stay from "~/images/hotel.svg";
import bicycle from "~/images/bicycle.svg";
import forbes from "~/images/Forbes_logo.svg.png";
import cambodia6 from "~/images/cambodia-bike-tour-6.jpeg";
import vietnam6 from "~/images/vietnam-bike-tour-4.jpeg";
import jordan6 from "~/images/jordan-cycling.jpeg";
import rajasthan6 from "~/images/rajasthan-bike-tour-4.jpg";
import rajasthan7 from "~/images/rajasthan-bike-tour-23.jpg";
import hokkaido6 from "~/images/east-hokkaido-cycling-east-coast.jpeg";
import cambodia7 from "~/images/cambodia-kep-cycling2.jpg";
import condenast from "~/images/Conde_Nast_Traveler_logo.svg.png";
import huffpost from "~/images/HuffPost.svg.png";
import facebook from "~/images/footer-facebook.svg";
import linkedin from "~/images/footer-linkedin.svg";
import instagram from "~/images/footer-instagram.svg";
import youtube from "~/images/footer-youtube.svg";
import { ChevronUpIcon } from "@heroicons/react/outline";
import ZohoCampaignFooter from "~/components/ZohoCampaignFooter";

/* ===== Static data outside render (no per-render allocations) ===== */
const destinations = [
	{ name: "Albania", to: "/destinations/albania-bike-tours" },
	{ name: "Austria", to: "/destinations/austria-bike-tours" },
	{ name: "Bali", to: "/destinations/bali-bike-tours" },
	{ name: "Bhutan", to: "/destinations/bhutan-cycling-tours" },
	{ name: "Botswana", to: "/destinations/botswana-bike-tours" },
	{ name: "Cambodia", to: "/destinations/cambodia-bike-tours" },
	{ name: "Chile", to: "/destinations/chile-bike-tours" },
	{ name: "Croatia", to: "/destinations/croatia-bike-tours" },
	{ name: "Czech Republic", to: "/destinations/czech-republic-bike-tours" },
	{ name: "France", to: "/destinations/france-bike-tours" },
	{ name: "Germany", to: "/destinations/germany-bike-tours" },
	{ name: "Greece", to: "/destinations/greece-bike-tours" },
	{ name: "Hungary", to: "/destinations/hungary-bike-tours" },
	{ name: "India", to: "/destinations/india-bicycle-tours" },
	{ name: "Ireland", to: "/destinations/ireland-bike-tours" },
	{ name: "Italy", to: "/destinations/italy-bike-tours" },
	{ name: "Japan", to: "/destinations/japan-bike-tours" },
	{ name: "Jordan", to: "/destinations/jordan-bike-tours" },
	{ name: "Laos", to: "/destinations/laos-bike-tours" },
	{ name: "Mongolia", to: "/destinations/mongolia-bike-tours" },
	{ name: "Montenegro", to: "/destinations/montenegro-bike-tours" },
	{ name: "Morocco", to: "/destinations/morocco-bike-tours" },
	{ name: "Nepal", to: "/destinations/nepal-bike-tours" },
	{ name: "Norway", to: "/destinations/norway-bike-tours" },
	{ name: "Philippines", to: "/destinations/philippines-bike-tours" },
	{ name: "Poland", to: "/destinations/poland-bike-tours" },
	{ name: "Portugal", to: "/destinations/portugal-bike-tours" },
	{ name: "Romania", to: "/destinations/romania-bike-tours" },
	{ name: "Slovakia", to: "/destinations/slovakia-bike-tours" },
	{ name: "Slovenia", to: "/destinations/slovenia-bike-tours" },
	{ name: "South Africa", to: "/destinations/south-africa-bike-tours" },
	{ name: "Spain", to: "/destinations/spain-bike-tours" },
	{ name: "Sri Lanka", to: "/destinations/sri-lanka-cycling-tours" },
	{ name: "Taiwan", to: "/destinations/taiwan-bike-tours" },
	{ name: "Thailand", to: "/destinations/thailand-bike-tours" },
	{ name: "Vietnam", to: "/destinations/vietnam-bike-tours" },
];
const hiking = [
	{ name: "Bhutan", to: "/destinations/bhutan-hiking-tours" },
	{ name: "Crotia", to: "/destinations/croatia-hiking-tours" },
	{ name: "Japan", to: "/destinations/japan-hiking-tours" },
	{ name: "Nepal", to: "/destinations/nepal-trekking-tours" },
	{ name: "Sri Lanka", to: "/destinations/sri-lanka-hiking-tours" },
	{ name: "Portugal", to: "/destinations/portugal-hiking-tours" },
	{ name: "Vietnam", to: "/destinations/vietnam-hiking-tours" },
];
const multi = [
	{ name: "Bhutan", to: "/destinations/bhutan-multi-adventure-tours" },
	{ name: "Cambodia", to: "/destinations/cambodia-multi-adventure-tours" },
	{ name: "Laos", to: "/destinations/laos-multi-adventure-tours" },
	{ name: "Sri Lanka", to: "/destinations/sri-lanka-multi-adventure-tours" },
	{ name: "Thailand", to: "/destinations/thailand-multi-adventure-tours" },
	{ name: "Vietnam", to: "/destinations/vietnam-multi-adventure-tours" },
];

const collections = [
	{ name: "Bike and Boat Tours", to: "/specialities/bike-and-boat-tours" },
	{ name: "Self-Guided Bike Tours", to: "/specialities/self-guided-bike-tours" },
	{ name: "Luxury Bike Tours", to: "/specialities/luxury-bike-tours" },
	{
		name: "E-bike Tours for Seniors",
		to: "/specialities/e-bike-tours-for-seniors/",
	},
	{ name: "Road Bike Tours", to: "/specialities/road-bike-tours/" },
	{ name: "Gravel Bike Tours", to: "/specialities/gravel-bike-tours/" },
	{ name: "Multi-sport Adventures", to: "/specialities/multi-sport-tours/" },
	{ name: "Family Bike Tours", to: "/specialities/family/" },
	{ name: "Private Trips", to: "/private/" },
];

const ourCompany = [
	{ name: "About Us", to: "/about-us/our-company/" },
	{ name: "Reviews", to: "/about-us/reviews/" },
	// { name: "Guaranteed Trips", to: "/guaranteed-bike-tours/" },
	{ name: "FAQs", to: "/faqs/" },
	{ name: "Expert Blog", to: "/blog/" },
	{ name: "Travel Advisors", to: "/travel-advisors/" },
	{ name: "Terms & Conditions", to: "/terms-conditions" },
	{ name: "Privacy Policy", to: "/privacy-policy" },
];

/* ===== Tiny in-view hook (SSR-safe) to defer decorative sections ===== */
function useInView(options) {
	const ref = useRef(null);
	const [inView, setInView] = React.useState(false);
	useEffect(() => {
		const el = ref.current;
		if (!el || typeof IntersectionObserver === "undefined") {
			setInView(true);
			return;
		}
		const obs = new IntersectionObserver(([entry]) => {
			if (entry.isIntersecting) {
				setInView(true);
				obs.disconnect();
			}
		}, options);
		obs.observe(el);
		return () => obs.disconnect();
	}, [options]);
	return [ref, inView];
}

/* ===== Mobile-only collapsible section (memoized) ===== */
const MobileSection = React.memo(function MobileSection({ title, children }) {
	return (
		<details className="md:hidden border-t border-gray-200">
			<summary className="list-none cursor-pointer select-none py-4 flex items-center justify-between">
				<span className="text-sm font-medium text-gray-800">{title}</span>
				<svg
					className="h-4 w-4 shrink-0"
					viewBox="0 0 20 20"
					fill="currentColor"
					aria-hidden="true"
				>
					<path
						fillRule="evenodd"
						d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 011.08 1.04l-4.25 4.25a.75.75 0 01-1.06 0L5.21 8.27a.75.75 0 01.02-1.06z"
						clipRule="evenodd"
					/>
				</svg>
			</summary>
			<div className="pb-4">{children}</div>
		</details>
	);
});

/* ===== Decorative gallery — mounts only when scrolled near it ===== */
function StayConnectedGallery() {
	const [ref, inView] = useInView({ rootMargin: "300px 0px" });
	return (
		<section className="my-20" ref={ref}>
			{inView ? (
				<div className="relative overflow-hidden bg-white">
					<div className="pb-80 pt-16 sm:pb-40 sm:pt-24 lg:pb-48 lg:pt-40">
						<div className="relative mx-auto max-w-7xl px-4 sm:static sm:px-6 lg:px-8">
							<div className="sm:max-w-lg">
								<h2 className="text-4xl uppercase font-bold tracking-tight text-primary sm:text-6xl">
									Stay connected
								</h2>
								<p className="mt-6 text-xl">
									<a
										href="https://www.instagram.com/artofbicycleglobal/"
										target="_blank"
										rel="noopener noreferrer"
										className="bg-insta py-2 px-4 sm:px-4 rounded-full text-white"
									>
										Follow Us on Instagram
									</a>
								</p>
							</div>

							<div className="my-20">
								<div
									aria-hidden="true"
									className="pointer-events-none lg:absolute lg:inset-y-0 lg:mx-auto lg:w-full lg:max-w-7xl"
								>
									<div className="absolute transform sm:left-1/2 sm:top-0 sm:translate-x-8 lg:left-1/2 lg:top-1/2 lg:-translate-y-1/2 lg:translate-x-8">
										<div className="flex items-center space-x-6 lg:space-x-8">
											{/* Column 1 */}
											<div className="grid flex-shrink-0 grid-cols-1 gap-y-6 lg:gap-y-8">
												<div className="h-64 w-44 overflow-hidden rounded-lg sm:opacity-0 lg:opacity-100">
													<img
														className="h-full w-full object-cover object-center"
														src={cambodia6}
														alt="Cycling in Cambodia"
														loading="lazy"
														decoding="async"
														width={176}
														height={256}
													/>
												</div>
												<div className="h-64 w-44 overflow-hidden rounded-lg">
													<img
														className="h-full w-full object-cover object-center"
														src={vietnam6}
														alt="Cycling in Vietnam"
														loading="lazy"
														decoding="async"
														width={176}
														height={256}
													/>
												</div>
											</div>
											{/* Column 2 */}
											<div className="grid flex-shrink-0 grid-cols-1 gap-y-6 lg:gap-y-8">
												<div className="h-64 w-44 overflow-hidden rounded-lg">
													<img
														className="h-full w-full object-cover object-center"
														src={jordan6}
														alt="Cycling in Jordan"
														loading="lazy"
														decoding="async"
														width={176}
														height={256}
													/>
												</div>
												<div className="h-64 w-44 overflow-hidden rounded-lg">
													<img
														className="h-full w-full object-cover object-center"
														src={rajasthan6}
														alt="Cycling in Rajasthan"
														loading="lazy"
														decoding="async"
														width={176}
														height={256}
													/>
												</div>
												<div className="h-64 w-44 overflow-hidden rounded-lg">
													<img
														className="h-full w-full object-cover object-center"
														src={rajasthan7}
														alt="Rajasthan ride"
														loading="lazy"
														decoding="async"
														width={176}
														height={256}
													/>
												</div>
											</div>
											{/* Column 3 */}
											<div className="grid flex-shrink-0 grid-cols-1 gap-y-6 lg:gap-y-8">
												<div className="h-64 w-44 overflow-hidden rounded-lg">
													<img
														className="h-full w-full object-cover object-center"
														src={hokkaido6}
														alt="Hokkaido coast cycling"
														loading="lazy"
														decoding="async"
														width={176}
														height={256}
													/>
												</div>
												<div className="h-64 w-44 overflow-hidden rounded-lg">
													<img
														className="h-full w-full object-cover object-center"
														src={cambodia7}
														alt="Kep cycling"
														loading="lazy"
														decoding="async"
														width={176}
														height={256}
													/>
												</div>
											</div>
										</div>
									</div>
								</div>
								{/* /decor grid */}
							</div>
						</div>
					</div>
				</div>
			) : null}
		</section>
	);
}

/* ===== Simple helper for icon links with consistent attrs ===== */
function SocialIcon({ href, src, alt }) {
	return (
		<a href={href} target="_blank" rel="noopener noreferrer nofollow">
			<img
				src={src}
				alt={alt}
				loading="lazy"
				decoding="async"
				width={28}
				height={28}
			/>
		</a>
	);
}

const Footer = React.memo(function Footer() {
	return (
		<footer>
			<hr />

			{/* What sets us apart */}
			<section>
				<div className="container px-5 py-24 mx-auto">
					<div className="text-center mb-20">
						<h2 className="mb-4">What Sets Us Apart</h2>
						<div className="flex mt-6 justify-center">
							<div className="w-16 h-1 rounded-full bg-primary inline-flex" />
						</div>
					</div>

					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
						{/* 1 */}
						<div className="flex flex-col items-center text-center p-4">
							<div className="w-32 h-32 inline-flex items-center justify-center bg-pink-50 rounded-full mb-5">
								<img
									className="w-16 h-12"
									src={itinerary}
									alt="itinerary icon"
									loading="lazy"
									decoding="async"
									width={64}
									height={48}
								/>
							</div>
							<h3 className="text-lg font-medium mb-3">Thoughtfully Crafted</h3>
							<p className="leading-relaxed font-light text-sm">
								Curated itineraries that flow seamlessly — rich in local flavor,
								comfort, and charm.
							</p>
						</div>
						{/* 2 */}
						<div className="flex flex-col items-center text-center p-4">
							<div className="w-32 h-32 inline-flex items-center justify-center bg-yellow-50 rounded-full mb-5">
								<img
									className="w-16 h-12"
									src={stay}
									alt="hotel icon"
									loading="lazy"
									decoding="async"
									width={64}
									height={48}
								/>
							</div>
							<h3 className="text-lg font-medium mb-3">Handpicked Hotels</h3>
							<p className="leading-relaxed font-light text-sm">
								Luxury and boutique stays that reflect local ethos, delivering an
								unforgettable experience.
							</p>
						</div>
						{/* 3 */}{" "}
						{/* <div className="p-4 md:w-1/2 lg:w-1/4 flex flex-col text-center items-center"> <div className="w-32 h-32 inline-flex items-center justify-center bg-green-50 rounded-full mb-5"> <img className="w-16 h-12" src={guide} alt="guide icon" loading="lazy" decoding="async" width={64} height={48} /> </div> <div className="flex-grow"> <h3 className="text-lg font-medium mb-3">Expert Local Guides</h3> <p className="leading-relaxed font-light text-sm"> The folks who steer you towards a unique and enriching experience, and sometimes truly a life-changing adventure. </p> </div> </div> */}
						{/* 3 (formerly 4) */}
						<div className="flex flex-col items-center text-center p-4">
							<div className="w-32 h-32 inline-flex items-center justify-center bg-blue-50 rounded-full mb-5">
								<img
									className="w-16 h-12"
									src={bicycle}
									alt="bicycle icon"
									loading="lazy"
									decoding="async"
									width={64}
									height={48}
								/>
							</div>
							<h3 className="text-lg font-medium mb-3">Top-quality Gear</h3>
							<p className="leading-relaxed font-light text-sm">
								Immerse in the adventure while we kit you out with reliable, top-notch
								equipment.
							</p>
						</div>
					</div>
				</div>
			</section>

			<hr />

			{/* Stay connected (deferred mount) */}
			<StayConnectedGallery />
			<hr />
			{/* Newsletter */}
			<section>
				<div className="my-20">
					<h2 className="text-center">Join 5000+ Active Adventurers</h2>
					{/* <p className="text-center my-4 max-w-xl mx-auto">
						Get access to our new trips, guaranteed departures, and adventure
						inspiration from our experts. Opt-out anytime.
					</p> */}
					<p className="text-center my-4 max-w-xl mx-auto">
						Join our newsletter to get inspiration for your next cycling and active
						adventure. Opt-out anytime.
					</p>
					<ZohoCampaignFooter />
				</div>
			</section>
			{/* Featured in + footer nav */}
			<section className="bg-gradient-to-r from-white to-secondary -mx-6 md:-mx-16">
				{/* Featured in */}
				<div className="mx-auto max-w-7xl px-6 lg:px-8 pt-10">
					<p className="text-center text-sm md:text-base font-semibold">
						Featured in
					</p>
					<div className="mx-auto mt-6 grid max-w-lg grid-cols-3 items-center gap-x-8 gap-y-10 sm:max-w-xl sm:grid-cols-3 sm:gap-x-10 lg:mx-0 lg:max-w-none lg:grid-cols-3">
						<a
							href="https://www.forbes.com/sites/jaredranahan/2023/08/31/five-stunning-cycling-routes-to-explore-across-asia/"
							target="_blank"
							rel="noopener noreferrer"
						>
							<img
								className="col-span-2 max-h-6 w-full object-contain lg:col-span-1"
								src={forbes}
								alt="Forbes"
								width={158}
								height={48}
								loading="lazy"
								decoding="async"
							/>
						</a>
						<a
							href="https://www.huffpost.com/entry/worlds-most-beautiful-bike-trails_b_5671555"
							target="_blank"
							rel="noopener noreferrer"
						>
							<img
								className="col-span-2 max-h-6 w-full object-contain lg:col-span-1"
								src={huffpost}
								alt="HuffPost"
								width={158}
								height={48}
								loading="lazy"
								decoding="async"
							/>
						</a>
						<a
							href="https://www.cntraveller.in/story/be-spoke-travel-india-s-top-cycling-routes/"
							target="_blank"
							rel="noopener noreferrer"
						>
							<img
								className="col-span-2 max-h-6 w-full object-contain lg:col-span-1"
								src={condenast}
								alt="Condé Nast Traveler"
								width={158}
								height={48}
								loading="lazy"
								decoding="async"
							/>
						</a>
					</div>
				</div>

				{/* Footer nav */}
				<div className="container px-5 py-24 mx-auto flex md:items-center lg:items-start md:flex-row md:flex-nowrap flex-wrap flex-col">
					{/* Mobile menus */}
					<div className="px-5 md:hidden w-full">
						<MobileSection title="Biking">
							<ul className="grid grid-cols-2 gap-x-4 gap-y-2">
								{destinations.map((d, i) => (
									<li key={i}>
										<Link
											to={d.to}
											className="block text-sm text-gray-700 hover:text-primary py-1.5"
										>
											{d.name}
										</Link>
									</li>
								))}
							</ul>
						</MobileSection>
						<MobileSection title="Hiking & Walking">
							<ul className="grid grid-cols-2 gap-x-4 gap-y-2">
								{hiking.map((d, i) => (
									<li key={i}>
										<Link
											to={d.to}
											className="block text-sm text-gray-700 hover:text-primary py-1.5"
										>
											{d.name}
										</Link>
									</li>
								))}
							</ul>
						</MobileSection>
						<MobileSection title="Multi-Adventure">
							<ul className="grid grid-cols-2 gap-x-4 gap-y-2">
								{multi.map((d, i) => (
									<li key={i}>
										<Link
											to={d.to}
											className="block text-sm text-gray-700 hover:text-primary py-1.5"
										>
											{d.name}
										</Link>
									</li>
								))}
							</ul>
						</MobileSection>

						{/* <MobileSection title="Collections">
							<ul className="list-none">
								{collections.map((c, index) => (
									<li key={index} className="mb-1">
										<Link to={c.to} className="hover:text-primary text-sm text-gray-700">
											{c.name}
										</Link>
									</li>
								))}
							</ul>
						</MobileSection> */}

						<MobileSection title="Our Company">
							<ul className="list-none">
								{ourCompany.map((item, index) => (
									<li key={index} className="mb-1">
										<Link
											to={item.to}
											className="hover:text-primary text-sm text-gray-700"
										>
											{item.name}
										</Link>
									</li>
								))}
							</ul>
						</MobileSection>

						<MobileSection title="Contact Us">
							<div className="py-2">
								<Link
									to="/contact-us?utm_source=footer&utm_medium=contact-us&utm_campaign=send-a-message"
									className="inline-block bg-primary uppercase font-semibold tracking-wide py-2 px-5 rounded-full text-white"
								>
									Send a Message
								</Link>
								<div className="mt-6">
									<p className="text-sm text-gray-600 uppercase mb-3">Call Us:</p>
									<p className="mb-3">
										<a href="tel:+1-707-590-5789" className="underline">
											US/CA: +1 707 590 5789
										</a>
									</p>
									<p className="mb-3">
										<a href="tel:+447448517563" className="underline">
											UK/EU: +44 74485 17563
										</a>
									</p>
									<p className="mb-1">
										<a href="tel:+918208924403" className="underline">
											AU/NZ: +91 8208924403
										</a>
									</p>
								</div>
							</div>
						</MobileSection>
					</div>

					{/* Desktop menus */}
					<div className="hidden md:flex flex-grow flex-wrap md:pl-20 -mb-10 md:mt-0 mt-10 md:text-left text-center">
						<div className="lg:w-1/5 md:w-1/3 w-full px-4">
							<h3 className="font-semibold tracking-wider uppercase text-sm mb-3">
								Biking
							</h3>
							<ul className="list-none mb-10">
								{destinations.map((d, index) => (
									<li key={index} className="mb-1">
										<Link to={d.to} className="hover:text-primary text-sm text-gray-600">
											{d.name}
										</Link>
									</li>
								))}
							</ul>
						</div>
						<div className="lg:w-1/5 md:w-1/3 w-full px-4">
							<h3 className="font-semibold uppercase tracking-wider text-sm mb-3">
								Hiking & Walking
							</h3>
							<ul className="list-none mb-10">
								{hiking.map((d, index) => (
									<li key={index} className="mb-1">
										<Link to={d.to} className="hover:text-primary text-sm text-gray-600">
											{d.name}
										</Link>
									</li>
								))}
							</ul>
						</div>
						<div className="lg:w-1/5 md:w-1/3 w-full px-4">
							<h3 className="font-semibold uppercase tracking-wider text-sm mb-3">
								Multi-Adventure
							</h3>
							<ul className="list-none mb-10">
								{multi.map((d, index) => (
									<li key={index} className="mb-1">
										<Link to={d.to} className="hover:text-primary text-sm text-gray-600">
											{d.name}
										</Link>
									</li>
								))}
							</ul>
						</div>

						{/* <div className="lg:w-1/4 md:w-1/2 w-full px-4">
							<h3 className="font-semibold tracking-wider text-sm mb-3">
								COLLECTIONS
							</h3>
							<ul className="list-none mb-10">
								{collections.map((c, index) => (
									<li key={index} className="mb-1">
										<Link to={c.to} className="hover:text-primary text-sm text-gray-600">
											{c.name}
										</Link>
									</li>
								))}
							</ul>
						</div> */}

						<div className="lg:w-1/5 md:w-1/3 w-full px-4">
							<h3 className="font-semibold tracking-wider text-sm mb-3">
								OUR COMPANY
							</h3>
							<ul className="list-none mb-10">
								{ourCompany.map((item, index) => (
									<li key={index} className="mb-1">
										<Link
											to={item.to}
											className="hover:text-primary text-sm text-gray-600"
										>
											{item.name}
										</Link>
									</li>
								))}
							</ul>
						</div>

						<div className="lg:w-1/5 md:w-1/3 w-full px-4">
							<h3 className="font-semibold tracking-wider text-sm mb-3">CONTACT</h3>
							<ul className="list-none mb-10">
								<li className="my-6 lg:block">
									<Link
										to="/contact-us?utm_source=footer&utm_medium=contact-us&utm_campaign=send-a-message"
										className="bg-primary font-semibold tracking-wide py-2 px-5 sm:px-4 rounded-full text-white"
									>
										Message Us
									</Link>
								</li>
								<li>
									<p className="text-sm text-gray-600 uppercase mb-4 mt-10">Call Us:</p>
									<p className="mb-4">
										<a href="tel:+1-707-590-5789">
											US/CA: <span className="underline">+1 707 590 5789</span>
										</a>
									</p>
									<p className="mb-4">
										<a href="tel:+447448517563">
											UK/EU: <span className="underline">+44 74485 17563</span>
										</a>
									</p>
									<p className="mb-1">
										<a href="tel:+918208924403">
											AU/NZ: <span className="underline">+91 8208924403</span>
										</a>
									</p>
								</li>
							</ul>
						</div>
					</div>
				</div>

				{/* Social + legal */}
				<div>
					<div className="flex justify-center space-x-2">
						<SocialIcon
							href="https://www.instagram.com/artofbicycleglobal/"
							src={instagram}
							alt="Instagram"
						/>
						<SocialIcon
							href="https://www.youtube.com/channel/UC10rhc0jnPaJZRd4BwJpT1g"
							src={youtube}
							alt="YouTube"
						/>
						<SocialIcon
							href="https://www.linkedin.com/company/art-of-bicycle-trips"
							src={linkedin}
							alt="LinkedIn"
						/>
						<SocialIcon
							href="https://www.facebook.com/artofbicycleglobal"
							src={facebook}
							alt="Facebook"
						/>
					</div>

					<div className="text-center py-4">
						<p className="text-sm text-gray-500">© 2025 Art of Bicycle Trips</p>
						<p className="text-sm text-gray-500 capitalize mx-4">
							Active Travels Pte Ltd, 32 Pekin Street #05-01 Singapore (048762)
						</p>
					</div>

					<div className="text-center pb-2">
						<a href="#top" className="text-sm hover:text-primary">
							Go to Top{" "}
							<ChevronUpIcon
								className="h-4 w-4 text-sm inline-flex ml-2"
								aria-hidden="true"
							/>
						</a>
					</div>
				</div>
			</section>
		</footer>
	);
});

export default Footer;
